import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDeleteFile, useRenameFile } from "@/hooks/queries";

export function RenameFileDialog({ file, onClose }) {
  const [name, setName] = useState(file?.name || "");
  const rename = useRenameFile();
  const save = async (e) => {
    e.preventDefault();
    try {
      await rename.mutateAsync({ id: file.id, name: name.trim() });
      toast.success("File renamed");
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <Dialog open={Boolean(file)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-ink-850 sm:max-w-md" data-testid="rename-file-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="text-white">Rename file</DialogTitle>
          </DialogHeader>
          <input autoFocus value={name} maxLength={255} onChange={(e) => setName(e.target.value)} data-testid="rename-file-input" className="mt-4 h-11 w-full rounded-xl border border-white/10 bg-ink-900 px-3 text-sm text-white focus:border-white/25 focus:outline-none" />
          <DialogFooter className="mt-5 gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300 hover:bg-white/[0.05]" data-testid="rename-file-cancel">Cancel</button>
            <button type="submit" disabled={!name.trim() || rename.isPending} className="rounded-full bg-white px-4 py-2 text-sm font-medium text-ink-950 disabled:opacity-50" data-testid="rename-file-save">Save</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteFileDialog({ file, onClose, onDeleted }) {
  const del = useDeleteFile();
  const confirm = async (e) => {
    e.preventDefault();
    try {
      await del.mutateAsync(file.id);
      toast.success("File deleted");
      onDeleted?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <AlertDialog open={Boolean(file)} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="border-white/10 bg-ink-850" data-testid="delete-file-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Delete this file?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">
            “{file?.name}” will be permanently removed from your account and from any chats it was attached to. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/[0.06] hover:text-white" data-testid="delete-file-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} disabled={del.isPending} className="bg-rose-500 text-white hover:bg-rose-500/90" data-testid="delete-file-confirm">
            {del.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
